import React from 'react';
import { Space } from 'antd';
import styled from 'styled-components';
import Button from '../Button';

/**
 * 표 위 도구 줄 (AMZUWEB-684).
 *
 * 여백 기준은 기존 `_amondz-table-summary`(주문/배송관리·멀티채널이 쓰는 것)다 —
 * 위아래 10px + 표와 5px. 이게 없으면 버튼이 표 헤더에 그대로 붙어
 * 어디까지가 도구고 어디부터가 표인지 경계가 사라진다.
 *
 * 다만 아래 간격은 5px 가 아니라 12px 다. 원본은 이 줄에 **글자만** 있어서 5px 로 충분했는데,
 * 여기는 버튼이 같이 서 있어 시각 무게가 커진다 — 같은 5px 를 주면 버튼이 표에 얹힌 것처럼 보인다.
 */
const StyledToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  margin-bottom: 12px;
`;

/** 총건수 강조 — 기존 `TableToolbar`·`ListBox` 의 `.cnt` 표현(보라 강조)을 승계한다 */
const StyledTotalCount = styled.span`
  font-size: 15px;
  font-weight: 600;

  .cnt {
    color: #6200ea;
  }
`;

type ListToolbarProps = {
  /** 총건수 — 왼쪽에 표기한다. 없으면 표기를 생략한다 */
  totalCount?: number;
  onClickCreate?: () => void;
  createText?: string;
  onClickExcelDownload?: () => void;
  isExcelDownloading?: boolean;
};

/**
 * 표 위 도구 영역 (AMZUWEB-684) — **버튼 순서를 고정한다.**
 *
 * 지금 admin 은 화면마다 `[엑셀][+등록]` 과 `[+등록][엑셀]` 이 섞여 있다.
 * 순서가 섞이면 손이 기억하는 위치가 화면마다 달라져 오클릭이 난다.
 * 여기서 `[+등록] → [엑셀 다운로드]` 로 못 박고, 권한이 없으면 핸들러를 주지 않는 쪽으로 감춘다.
 */
export const ListToolbar = ({
  totalCount,
  onClickCreate,
  createText = '+ 등록',
  onClickExcelDownload,
  isExcelDownloading = false,
}: ListToolbarProps): React.JSX.Element => (
  <StyledToolbar>
    {/*
      문구를 어드민 관례에 맞춘다 — `ListBox`·`TableToolbar`·`SelectListModal` 이
      전부 "N건이 조회되었습니다." 다. 여기만 "총 N건" 이면 같은 뜻인데 다른 말이 된다.
    */}
    <StyledTotalCount>
      {totalCount === undefined ? null : (
        <>
          <span className="cnt">{totalCount.toLocaleString()}</span>건이 조회되었습니다.
        </>
      )}
    </StyledTotalCount>

    <Space>
      {onClickCreate && <Button isRadius={true} color="primary" text={createText} onClick={onClickCreate} />}
      {onClickExcelDownload && (
        <Button
          isRadius={true}
          color="primary"
          text={isExcelDownloading ? '요청 중...' : '엑셀다운'}
          disabled={isExcelDownloading}
          onClick={onClickExcelDownload}
        />
      )}
    </Space>
  </StyledToolbar>
);
